import { anthropicMessagesUsage } from "../anthropic/usage";
import {
  configuredModelsEndpoint,
  connectorAllowedModels,
  MODELS_PREFIX,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const FOUNDRY_HOST_PATTERN = "[a-z0-9-]+\\.services\\.ai\\.azure\\.com";

// Scoped to the surface path, not just the host: both variants share a
// resource, so the path is the only thing that tells them apart.
const surfacePattern =
  (surface: "anthropic" | "openai") =>
  ({ baseUrl }: Record<string, string>) => {
    const host = (baseUrl ?? "")
      .trim()
      .replace(/^https?:\/\//, "")
      .split("/")[0];
    return host
      ? domainToRegex(`${host}/${surface}`)
      : `https://${FOUNDRY_HOST_PATTERN}/${surface}/.*`;
  };

// Foundry serves no models API, so the Claude connection test posts to
// /messages a body the API has to reject for its missing fields, and reads the
// 400 as proof the surface is reachable and took the key. It still names a
// configured deployment, because the proxy's allowlist fails closed on a POST
// to /messages carrying no readable top-level model.
const claudeProbeBody = (configuration: Record<string, string>) => {
  const [deployment] = connectorAllowedModels(configuration);
  return JSON.stringify(deployment ? { model: deployment } : {});
};

const KEYWORDS =
  "microsoft foundry azure ai foundry azure ai azure openai claude anthropic gpt grok deepseek llama llm models inference";

const portalMarkdown =
  "In the [Azure AI Foundry portal](https://ai.azure.com), open your Foundry resource and deploy the models you want under **Deployments**. Then copy the resource endpoint and one of its API keys from **Overview → Keys and Endpoint**.";

export const microsoftFoundry = defineProduct({
  name: "Microsoft Foundry",
  tagLine: "Claude, GPT, and partner models on Azure",
  logo,
  tags: ["ai", "engineering"],
  variants: [
    productVariant({
      identifier: "microsoftFoundryClaude",
      authLabel: "Claude",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: surfacePattern("anthropic"),
      auth: (app) =>
        headerAuth([
          { name: "anthropic-version", value: "2023-06-01" },
          { name: "x-api-key", value: app.secret.apiKey },
        ]),
      steps: [
        {
          title: "Deploy Claude models in Microsoft Foundry",
          items: [{ kind: "markdown", content: portalMarkdown }],
        },
        {
          title: "Enter your Foundry details",
          items: [
            {
              kind: "url",
              label: "Base URL",
              placeholder:
                "https://your-resource.services.ai.azure.com/anthropic/v1",
              required: true,
              target: { kind: "configuration", field: "baseUrl" },
              helperText:
                "Your Foundry resource endpoint including the /anthropic/v1 path. Claude on Foundry speaks the Anthropic Messages API, not the OpenAI-compatible surface.",
            },
            {
              kind: "input",
              label: "API key",
              placeholder: "…",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
          ],
        },
        {
          title: "Add your model deployments",
          items: [
            {
              kind: "list",
              label: "Available model deployments",
              prefix: MODELS_PREFIX,
              columns: [{ field: "name", placeholder: "claude-opus-5" }],
              addLabel: "Add deployment",
              required: true,
              helperText:
                "Enter at least one deployment name, matching a Claude model you deployed in Microsoft Foundry.",
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: (configuration) => ({
          url: `${(configuration.baseUrl ?? "").replace(/\/$/, "")}/messages`,
          method: "POST",
          headers: { "content-type": "application/json" },
          body: claudeProbeBody(configuration),
          expectedStatus: 400,
        }),
      },
      publicEnvVars: ({ baseUrl }) => ({
        ANTHROPIC_BASE_URL: baseUrl || "",
      }),
      usageHint: (configuration) =>
        withAllowedModelsHint(
          "Use process.env.ANTHROPIC_BASE_URL as the Anthropic API base URL; it targets the Foundry resource's /anthropic/v1 surface, which serves the standard Anthropic Messages API. The model parameter carries the deployment name. Pass a placeholder API key; the proxy injects the real x-api-key header from the connector.",
          configuration
        ),
      usage: anthropicMessagesUsage(),
    }),
    productVariant({
      identifier: "microsoftFoundryOpenai",
      authLabel: "OpenAI-compatible",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: surfacePattern("openai"),
      auth: (app) =>
        headerAuth([{ name: "api-key", value: app.secret.apiKey }]),
      steps: [
        {
          title: "Deploy models in Microsoft Foundry",
          items: [{ kind: "markdown", content: portalMarkdown }],
        },
        {
          title: "Enter your Foundry details",
          items: [
            {
              kind: "url",
              label: "Base URL",
              placeholder:
                "https://your-resource.services.ai.azure.com/openai/v1",
              required: true,
              target: { kind: "configuration", field: "baseUrl" },
              helperText:
                "Your Foundry resource endpoint including the /openai/v1 path. This surface serves GPT, Grok, DeepSeek, and Llama. Claude is not available here; use the Claude connection type instead, and note that models Foundry serves only on its older /models route are out of reach from this one.",
            },
            {
              kind: "input",
              label: "API key",
              placeholder: "…",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
          ],
        },
        {
          title: "Add your model deployments",
          items: [
            {
              kind: "list",
              label: "Available model deployments",
              prefix: MODELS_PREFIX,
              columns: [{ field: "name", placeholder: "gpt-5" }],
              addLabel: "Add deployment",
              required: true,
              helperText:
                "Enter at least one deployment name, matching a model you deployed in Microsoft Foundry.",
            },
          ],
        },
      ],
      connectionTest: { protocol: "http", request: configuredModelsEndpoint },
      publicEnvVars: ({ baseUrl }) => ({
        AZURE_OPENAI_BASE_URL: baseUrl || "",
      }),
      usageHint: (configuration) =>
        withAllowedModelsHint(
          "Use process.env.AZURE_OPENAI_BASE_URL as the OpenAI base URL; it targets the Foundry resource's /openai/v1 surface (no api-version needed). Call the Responses API (/responses), never /chat/completions. The model parameter carries the deployment name. Pass a placeholder API key; the proxy injects the real api-key header from the connector.",
          configuration
        ),
      usage: openaiUsage(),
    }),
  ],
});
